import React, { useEffect, useState } from "react";
import { Button, Form, Table, Alert, Card } from "react-bootstrap";
import axios from "axios";
import Layout from '../../components/layouts/Main';
import config from '../../config';
import { toast } from 'react-toastify';
import { Link, useNavigate } from "react-router-dom";
import { useHasPermission, PERMISSIONS } from "../../utils/permissions";
import PaymentTermsSelect from "../../components/common/PaymentTermsSelect";
import { nanoid } from "nanoid";

export default function AddPurchaseOrder() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState({
    vendorId: "",
    orderDate: new Date().toISOString().split("T")[0],
    expectedDeliveryDate: "",
    deliveryFrom: "",
    deliveryTo: "",
    tax: 0,
    notes: "",
    paymentTerm: "",
    itemsTotalPrice: 0
  });

  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({
    productId: "",
    quality: "",
    orderedQuantity: 0,
    unit: "",
    rate: 0,
    totalPrice: 0
  });

  const [vendors, setVendors] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [products, setProducts] = useState([]);

  // Permission checks
  const canCreatePO = useHasPermission(PERMISSIONS.PO_CREATE);
  const canViewVendors = useHasPermission(PERMISSIONS.VIEW_VENDORS);
  const canViewBrokers = useHasPermission(PERMISSIONS.VIEW_BROKERS);
  const canViewProducts = useHasPermission(PERMISSIONS.VIEW_PRODUCTS);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises = [];

        if (canViewVendors) {
          promises.push(axios.get(`${config.API_BASE_URL}/vendors?approved=true&status=Active`));
        }

        if (canViewBrokers) {
          promises.push(axios.get(`${config.API_BASE_URL}/brokers?approved=true&status=Active`));
        }

        if (canViewProducts) {
          promises.push(axios.get(`${config.API_BASE_URL}/products?populateUnit=true`));
        }

        const responses = await Promise.all(promises);

        let responseIndex = 0;
        if (canViewVendors) {
          setVendors(responses[responseIndex].data);
          responseIndex++;
        }

        if (canViewBrokers) {
          setBrokers(responses[responseIndex].data);
          responseIndex++;
        }

        if (canViewProducts) {
          setProducts(responses[responseIndex].data);
        }

        // Auto-fill delivery address
        const companySettingsRes = await axios.get(`${config.API_BASE_URL}/company-settings`);
        if (companySettingsRes.data?.address) {
          const addr = companySettingsRes.data.address;
          setOrderData(prev => ({
            ...prev,
            deliveryTo: `${addr.street}\n${addr.city} ${addr.state}\n${addr.pincode}`
          }));
        }

      } catch (err) {
        console.error("Error fetching data:", err);
        toast.error("Failed to load necessary data");
      }
    };

    fetchData();
  }, [canViewVendors, canViewBrokers, canViewProducts]);

  // Auto-fill vendor address when vendor is selected
  useEffect(() => {
    const selectedVendor = vendors.find((v) => v._id === orderData.vendorId);

    if (selectedVendor) {
      const formattedAddress = `${selectedVendor.business_name}\n${selectedVendor.address}\n${selectedVendor.town}, ${selectedVendor.city}, ${selectedVendor.state} - ${selectedVendor.pin_code}`;

      setOrderData((prev) => ({
        ...prev,
        deliveryFrom: formattedAddress,
      }));
    }
  }, [orderData.vendorId, vendors]);

  const handleOrderChange = (e) => {
    setOrderData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    let updatedItem = { ...newItem, [name]: value };

    // When product changes, auto-fill unit
    if (name === "productId") {
      const prod = products.find(p => p._id === value);
      updatedItem.unit = prod?.unit || "";
    }

    // Auto-calc total price when qty or rate changes
    if (name === "orderedQuantity" || name === "rate") {
      const qty = name === "orderedQuantity" ? value : updatedItem.orderedQuantity;
      const rate = name === "rate" ? value : updatedItem.rate;
      updatedItem.totalPrice = parseFloat(qty || 0) * parseFloat(rate || 0);
    }

    setNewItem(updatedItem);
  };

  const addItem = () => {
    if (!newItem.productId || newItem.orderedQuantity <= 0 || newItem.rate <= 0) {
      toast.error("Please fill product, quantity, and unit price");
      return;
    }

    setItems(prev => [
      ...prev,
      { ...newItem, tempId: nanoid() } // ✅ unique & stable
    ]);

    setNewItem({
      productId: "",
      quality: "",
      orderedQuantity: 0,
      unit: "",
      rate: 0,
      totalPrice: 0
    });
  };


  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const validateStep1 = () => {
    if (!orderData.vendorId) {
      toast.error("Please select a vendor");
      return false;
    }
    if (!orderData.expectedDeliveryDate) {
      toast.error("Please select expected delivery date");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (items.length === 0) {
      toast.error("Please add at least one item");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!canCreatePO) {
      toast.error("You don't have permission to create purchase orders");
      return;
    }

    if (!validateStep2()) return;

    setLoading(true);

    try {
      const subtotal = items.reduce((sum, it) => sum + (it.orderedQuantity * it.rate), 0);
      const taxAmount = subtotal * ((parseFloat(orderData.tax) || 0) / 100);
      const grandTotal = subtotal + taxAmount;

      const orderDataWithTotal = {
        ...orderData,
        itemsTotalPrice: grandTotal
      };

      const res = await axios.post(
        `${config.API_BASE_URL}/purchase-orders/new`,
        orderDataWithTotal
      );

      const orderId = res.data.order._id;

      await Promise.all(
        items.map(it =>
          axios.post(`${config.API_BASE_URL}/purchase-order-items/new`, {
            ...it,
            purchaseOrderId: orderId
          })
        )
      );


      setLoading(false);
      sessionStorage.setItem("pocreationSuccess", "true");
      navigate("/purchase-orders", { replace: true });
      return;

    } catch (err) {
      console.error("Error saving purchase order", err);
      setLoading(false);
      toast.error("Error saving purchase order. Please try again.");
    }
  };

  // If user can't create PO, show permission denied
  if (!canCreatePO) {
    return (
      <Layout>
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/purchase-orders">Purchase Orders</Link></li>
            <li className="breadcrumb-item active">Add New</li>
          </ol>
        </nav>
        <Alert variant="danger" className="d-flex align-items-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div>
            <strong>Access Denied</strong>
            <p className="mb-0">You don't have permission to create purchase orders. Contact your administrator for access.</p>
          </div>
        </Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/purchase-orders">Purchase Orders</Link></li>
          <li className="breadcrumb-item active">Add New</li>
        </ol>
      </nav>

      <div className="row mb-3">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">Add Purchase Order</h2>
              <small className="text-muted">Step {step} of 2</small>
            </div>
            <div className="progress" style={{ width: '200px', height: '6px' }}>
              <div
                className="progress-bar"
                style={{ width: `${(step / 2) * 100}%` }}
              ></div>
            </div>
          </div>

          {step === 1 && (
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-info-circle me-2"></i>Order Information
                </h5>
              </Card.Header>
              <Card.Body>
                <Form>
                  <div className="row">
                    <Form.Group className="col-md-4 mb-3">
                      <Form.Label>
                        Vendor <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="vendorId"
                        value={orderData.vendorId}
                        onChange={handleOrderChange}
                        disabled={!canViewVendors}
                      >
                        <option value="">Select Vendor...</option>
                        {vendors.map(v => (
                          <option key={v._id} value={v._id}>{v.business_name}</option>
                        ))}
                      </Form.Select>
                      {!canViewVendors && (
                        <Form.Text className="text-warning">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          Limited vendor access - contact admin
                        </Form.Text>
                      )}
                    </Form.Group>

                    <Form.Group className="col-md-4 mb-3">
                      <Form.Label>Broker (Optional)</Form.Label>
                      <Form.Select
                        name="brokerId"
                        value={orderData.brokerId}
                        onChange={handleOrderChange}
                        disabled={!canViewBrokers}
                      >
                        <option value="">Select Broker...</option>
                        {brokers.map(b => (
                          <option key={b._id} value={b._id}>{b.business_name}</option>
                        ))}
                      </Form.Select>
                      {!canViewBrokers && (
                        <Form.Text className="text-warning">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          Limited broker access - contact admin
                        </Form.Text>
                      )}
                    </Form.Group>

                    <Form.Group className="col-md-4 mb-3">
                      <Form.Label>Order Date <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        name="orderDate"
                        value={orderData.orderDate}
                        onChange={handleOrderChange}
                      />
                    </Form.Group>

                    <Form.Group className="col-md-4 mb-3">
                      <Form.Label>
                        Expected Delivery Date <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="expectedDeliveryDate"
                        value={orderData.expectedDeliveryDate}
                        onChange={handleOrderChange}
                      // min={new Date().toISOString().split("T")[0]}
                      />
                    </Form.Group>

                    <Form.Group className="col-md-4 mb-3">
                      <PaymentTermsSelect
                        value={orderData.paymentTerm}
                        onChange={(value) => setOrderData(prev => ({ ...prev, paymentTerm: value }))}
                      />
                    </Form.Group>
                  </div>

                  <div className="row">
                    <Form.Group className="col-md-6 mb-3">
                      <Form.Label>Delivery To</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={6}
                        name="deliveryTo"
                        value={orderData.deliveryTo}
                        onChange={handleOrderChange}
                        placeholder="Enter delivery address"
                      />
                    </Form.Group>

                    <Form.Group className="col-md-6 mb-3">
                      <Form.Label>Delivery From</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={6}
                        name="deliveryFrom"
                        value={orderData.deliveryFrom}
                        onChange={handleOrderChange}
                        placeholder="Enter pickup address"
                      />
                    </Form.Group>
                  </div>

                  <div className="row">

                    <Form.Group className="col-md-12 mb-3">
                      <Form.Label>Other Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="notes"
                        value={orderData.notes}
                        onChange={handleOrderChange}
                        placeholder="Additional notes or instructions"
                      />
                    </Form.Group>
                  </div>

                  <div className="d-flex justify-content-between">
                    <Link to="/purchase-orders" className="btn btn-secondary">
                      <i className="fas fa-arrow-left me-2"></i>Cancel
                    </Link>
                    <Button
                      onClick={() => {
                        if (validateStep1()) {
                          setStep(2);
                        }
                      }}
                      className="btn btn-primary"
                    >
                      Next: Add Items <i className="fas fa-arrow-right ms-2"></i>
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          )}

          {step === 2 && (
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-shopping-cart me-2"></i>Order Items
                </h5>
              </Card.Header>
              <Card.Body>
                {!canViewProducts && (
                  <Alert variant="warning">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    You have limited access to products. Contact your administrator for full product access.
                  </Alert>
                )}

                <Form className="mb-4 border rounded p-3 bg-light">
                  <h6 className="mb-3">Add Item</h6>
                  <div className="row g-2 align-items-end">
                    <div className="col-md-3 form-floating">
                      <Form.Select
                        id="productSelect"
                        name="productId"
                        value={newItem.productId}
                        onChange={(e) => {
                          const productId = e.target.value;
                          const selectedProduct = products.find(p => p._id === productId);
                          setNewItem(prev => ({
                            ...prev,
                            productId,
                            unit: selectedProduct?.unitId?.unitCode || ""
                          }));
                        }}
                        disabled={!canViewProducts}
                      >
                        <option value="">Select Product</option>
                        {products.map(p => (
                          <option key={p._id} value={p._id}>{p.productName}</option>
                        ))}
                      </Form.Select>
                      <label htmlFor="productSelect">Product *</label>
                    </div>

                    <div className="col-md-2 form-floating">
                      <Form.Control
                        id="qualityInput"
                        name="quality"
                        placeholder="Quality"
                        value={newItem.quality}
                        onChange={handleItemChange}
                      />
                      <label htmlFor="qualityInput">Quality</label>
                    </div>

                    <div className="col-md-2 position-relative">
                      <div className="form-floating">
                        <Form.Control
                          id="quantityInput"
                          type="number"
                          name="orderedQuantity"
                          placeholder="Quantity"
                          value={newItem.orderedQuantity}
                          onChange={handleItemChange}
                          min="0"
                          step="0.01"
                        />
                        <label htmlFor="quantityInput">Quantity *</label>
                      </div>
                      {newItem.unit && (
                        <span
                          className="unit-suffix"
                        >
                          {newItem.unit}
                        </span>
                      )}
                    </div>

                    <div className="col-md-2 form-floating">
                      <Form.Control
                        id="rateInput"
                        type="number"
                        name="rate"
                        placeholder="Unit Price"
                        value={newItem.rate}
                        onChange={handleItemChange}
                        min="0"
                        step="0.01"
                      />
                      <label htmlFor="rateInput">Unit Price (₹) *</label>
                    </div>

                    <div className="col-md-2 form-floating">
                      <Form.Control
                        id="totalPriceInput"
                        type="number"
                        value={(newItem.orderedQuantity * newItem.rate).toFixed(2)}
                        readOnly
                        className="bg-light"
                      />
                      <label htmlFor="totalPriceInput">Total (₹)</label>
                    </div>

                    <div className="col-md-1">
                      <Button
                        variant="success"
                        onClick={addItem}
                        className="w-100"
                        disabled={!canViewProducts}
                        title={!canViewProducts ? "Limited product access" : "Add item"}
                      >
                        <i className="fas fa-plus"></i>
                      </Button>
                    </div>
                  </div>
                </Form>

                {items.length > 0 && (
                  <>
                    <div className="table-responsive">
                      <Table bordered hover className="mb-4">
                        <thead className="table-light">
                          <tr>
                            <th>Product</th>
                            <th>Quality</th>
                            <th>Quantity</th>
                            <th>Unit Price</th>
                            <th>Total</th>
                            <th width="80px">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((it, i) => (
                            <tr key={it._id || it.tempId}>
                              <td>{products.find(p => p._id === it.productId)?.productName || 'N/A'}</td>
                              <td>{it.quality || '-'}</td>
                              <td>{it.orderedQuantity} {it.unit}</td>
                              <td>₹{parseFloat(it.rate).toFixed(2)}</td>
                              <td>₹{(it.orderedQuantity * it.rate).toFixed(2)}</td>
                              <td className="text-center">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => removeItem(i)}
                                  title="Remove item"
                                >
                                  <i className="fas fa-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    <div className="row justify-content-end">
                      <div className="col-md-6">
                        <Card>
                          <Card.Body>
                            <div className="row mb-2">
                              <div className="col-md-6">
                                <Form.Group>
                                  <Form.Label>Tax (%)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="tax"
                                    value={orderData.tax}
                                    onChange={handleOrderChange}
                                    min="0"
                                    max="100"
                                    step="0.01"
                                  />
                                </Form.Group>
                              </div>
                              <div className="col-md-6 d-flex align-items-end">
                                <div className="w-100">
                                  <Form.Label>Subtotal: ₹{items.reduce((sum, it) => sum + (it.orderedQuantity * it.rate), 0).toFixed(2)}</Form.Label>
                                </div>
                              </div>
                            </div>
                            <hr />
                            <div className="d-flex justify-content-between align-items-center">
                              <strong>Grand Total:</strong>
                              <strong className="text-primary fs-4">
                                ₹{(() => {
                                  const subtotal = items.reduce((sum, it) => sum + (it.orderedQuantity * it.rate), 0);
                                  const taxAmount = subtotal * ((parseFloat(orderData.tax) || 0) / 100);
                                  return (subtotal + taxAmount).toFixed(2);
                                })()}
                              </strong>
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                    </div>
                  </>
                )}

                {items.length === 0 && (
                  <div className="text-center py-4 text-muted">
                    <i className="fas fa-shopping-cart fa-2x mb-2"></i>
                    <p>No items added yet. Add items to continue.</p>
                  </div>
                )}

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStep(1)}
                  >
                    <i className="fas fa-arrow-left me-2"></i>Back
                  </Button>
                  <Button
                    variant="success"
                    onClick={handleSave}
                    disabled={items.length === 0 || loading}
                  >
                    {/* {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Creating...
                      </>
                    ) : (
                      <> */}
                    <span className="fas fa-save me-2"></span>Create Purchase Order
                    {/* </>
                    )} */}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}
